import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useToast } from '../sectepe-ui/index';
import { workflowApi, generationApi } from '../services/api';
import { WorkflowErgebnisse } from '../inhalte/WorkflowErgebnisse';
import ChatPanel from '../components/ChatPanel';
import { PageHeader, EmptyState, LoadingState } from '../components/ui';
import { Icon } from '../components/icons';
import { fehlerText } from '../vertrieb/format';
import { schritteAus, variablenAus, variablenLabel } from '../inhalte/workflowSchritte';

const ANBIETER: Record<string, string> = { openai: 'OpenAI', claude: 'Claude', perplexity: 'Perplexity' };

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const einsetzen = (text: string, vars: Record<string, string>) =>
  Object.entries(vars).reduce((out, [k, v]) => out.replace(new RegExp(`\\{\\{${escapeRegex(k)}\\}\\}`, 'g'), v ?? ''), text);

/**
 * Workflow Schritt fuer Schritt ausfuehren. Nach jedem Schritt laesst sich
 * das Ergebnis im Chat nachschaerfen; erst die Bestaetigung gibt es an den
 * naechsten Schritt weiter.
 */
const ExecuteWorkflowPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { push } = useToast();
  const [workflow, setWorkflow] = useState<any>(null);
  const [variablen, setVariablen] = useState<Record<string, string>>({});
  const [ergebnisse, setErgebnisse] = useState<Record<string, any>>({});
  const [index, setIndex] = useState(0);
  const [laeuft, setLaeuft] = useState(false);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState(false);

  const laden = useCallback(async () => {
    try {
      const daten = await workflowApi.getWorkflow(id!);
      if (!daten) return;
      setWorkflow(daten);
      const namen = variablenAus(schritteAus(daten).map((s) => s.prompt));
      setVariablen(Object.fromEntries(namen.map((n) => [n, ''])));
    } catch (e) {
      setFehler(fehlerText(e, 'Workflow konnte nicht geladen werden'));
    } finally {
      setLaedt(false);
    }
  }, [id]);

  useEffect(() => { laden(); }, [laden]);

  if (laedt) return <div className="page-container"><LoadingState rows={4} label="Workflow wird geladen" /></div>;

  if (!workflow) {
    return (
      <div className="page-container">
        <EmptyState icon="workflow" title="Workflow nicht gefunden" description={fehler ?? 'Vielleicht wurde er gelöscht.'} action={{ label: 'Meine Workflows', to: '/workflows' }} />
      </div>
    );
  }

  const schritte = schritteAus(workflow);
  const schritt = schritte[index];
  const eingabeNamen = Object.keys(variablen).filter((n) => !n.startsWith('step_'));
  const fehlend = eingabeNamen.filter((n) => !variablen[n]?.trim());

  const ausfuehren = async () => {
    if (!schritt) return;
    if (fehlend.length) {
      setFehler(`Bitte noch ausfüllen: ${fehlend.map(variablenLabel).join(', ')}.`);
      return;
    }
    setLaeuft(true);
    setFehler(null);
    try {
      const antwort = await workflowApi.executeWorkflowStep(id!, { variables: variablen, stepIndex: index });
      const { step, variables: angereichert } = antwort;
      setErgebnisse((alt) => ({ ...alt, [step.stepId]: step }));
      setVariablen((alt) => ({ ...alt, ...angereichert }));
    } catch (e) {
      setFehler(fehlerText(e, 'Der Schritt ist fehlgeschlagen'));
    } finally {
      setLaeuft(false);
    }
  };

  const bestaetigen = async (text: string) => {
    const s = schritte[index];
    // Per Chat ueberarbeitet: auch das gespeicherte Ergebnis anpassen, nicht nur
    // den Text fuer den naechsten Schritt (bisher blieb das Original gespeichert).
    const gespeichert = ergebnisse[s.id]?.savedContentId;
    if (text && gespeichert && text !== ergebnisse[s.id]?.content) {
      try {
        await generationApi.updateContent(gespeichert, text);
      } catch (e) {
        setFehler(fehlerText(e, 'Die Überarbeitung konnte nicht gespeichert werden'));
        return;
      }
    }
    setVariablen((alt) => ({ ...alt, [`step_${s.id}_result`]: text || ergebnisse[s.id]?.content }));
    if (text) setErgebnisse((alt) => ({ ...alt, [s.id]: { ...alt[s.id], content: text } }));
    if (index < schritte.length - 1) {
      setIndex((i) => i + 1);
    } else {
      setFertig(true);
      push({ message: `„${workflow.name}“ abgeschlossen.`, severity: 'success' });
    }
  };

  const erledigt = (i: number) => fertig || i < index || Boolean(ergebnisse[schritte[i]?.id]);

  return (
    <div className="page-container">
      <PageHeader
        eyebrow={<Link to="/workflows"><Icon name="arrow-left" size={12} /> Meine Workflows</Link>}
        title={workflow.name}
        subtitle={workflow.description || undefined}
        actions={<Link to={`/workflows/${workflow.id}/edit`} className="btn btn-secondary"><Icon name="edit" size={14} /> Bearbeiten</Link>}
      />

      {schritte.length === 0 ? (
        <EmptyState icon="workflow" title="Dieser Workflow hat keine Schritte" action={{ label: 'Schritte anlegen', to: `/workflows/${workflow.id}/edit` }} />
      ) : (
        <>
          <ol className="durchlauf-schritte" aria-label="Schritte">
            {schritte.map((s, i) => (
              <li key={s.id} className="durchlauf-schritt" data-state={erledigt(i) ? 'done' : i === index ? 'ready' : 'blocked'} aria-current={!fertig && i === index ? 'step' : undefined}>
                <span className="durchlauf-schritt__nr">{erledigt(i) ? <Icon name="check" size={14} /> : i + 1}</span>
                <span className="durchlauf-schritt__text"><strong>{s.name}</strong><small>{ANBIETER[s.provider] ?? s.provider}</small></span>
              </li>
            ))}
          </ol>

          {fertig && (
            <div className="flow-banner flow-banner--done" role="status">
              <Icon name="check" size={16} />
              <span>Alle {schritte.length} Schritte abgeschlossen. Die Ergebnisse stehen unten.</span>
              <Link to="/workflows" className="btn btn-primary btn-sm">Zu den Workflows</Link>
            </div>
          )}
          {fertig && (
            <section className="card" aria-label="Gespeichert">
              <h2 className="card-title">Gespeichert und einplanbar</h2>
              <WorkflowErgebnisse workflowId={workflow.id} limit={schritte.length || 5} />
            </section>
          )}

          {eingabeNamen.length > 0 && (
            <section className="card inhalt-formular" aria-label="Angaben">
              <h2 className="card-title">Angaben</h2>
              <div className="inhalt-felder">
                {eingabeNamen.map((n) => (
                  <label key={n}>
                    {variablenLabel(n)} *
                    <input type="text" value={variablen[n]} aria-invalid={Boolean(fehler) && !variablen[n]?.trim() ? true : undefined} onChange={(e) => setVariablen({ ...variablen, [n]: e.target.value })} disabled={index > 0 || fertig} />
                  </label>
                ))}
              </div>
            </section>
          )}

          {!fertig && schritt && (
            <section className="card" aria-label={`Schritt ${index + 1}`}>
              <h2 className="card-title">Schritt {index + 1} von {schritte.length}: {schritt.name}</h2>
              <details className="inhalt-details">
                <summary>Prompt ansehen</summary>
                <pre className="code-block">{einsetzen(schritt.prompt, variablen)}</pre>
              </details>
              {fehler && <div className="alert alert-error">{fehler}</div>}
              {ergebnisse[schritt.id] ? (
                <ChatPanel
                  key={schritt.id}
                  provider={schritt.provider}
                  workflowId={workflow.id}
                  initialMessages={[
                    { role: 'system', content: 'Du bist ein präziser deutscher Editor. Verbessere Stil, Klarheit, Struktur und Kürze, ohne inhaltliche Kernaussagen zu verändern.' },
                    { role: 'assistant', content: ergebnisse[schritt.id].content || '' },
                  ]}
                  onConfirm={bestaetigen}
                />
              ) : (
                <div className="btn-group">
                  <button type="button" className="btn btn-primary" onClick={ausfuehren} disabled={laeuft}>
                    <Icon name="play" size={14} /> {laeuft ? 'Läuft…' : 'Schritt ausführen'}
                  </button>
                  {index > 0 && (
                    <button type="button" className="btn btn-ghost" onClick={() => { setFehler(null); setIndex((i) => i - 1); }} disabled={laeuft}>
                      <Icon name="arrow-left" size={14} /> Zurück
                    </button>
                  )}
                </div>
              )}
            </section>
          )}

          {Object.keys(ergebnisse).length > 0 && (
            <section aria-label="Ergebnisse">
              <h2 className="flow-section-title">Ergebnisse</h2>
              {schritte.map((s, i) => {
                const r = ergebnisse[s.id];
                if (!r) return null;
                return (
                  <article key={s.id} className="card">
                    <h3 className="card-title">Schritt {i + 1}: {r.stepName || s.name}</h3>
                    <p className="text-xs text-secondary">{ANBIETER[r.provider] ?? r.provider}{r.model && <> · {r.model}</>}{r.tokensUsed && <> · {r.tokensUsed} Tokens</>}</p>
                    <div className="result-container"><ReactMarkdown remarkPlugins={[remarkGfm]}>{r.content || ''}</ReactMarkdown></div>
                  </article>
                );
              })}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default ExecuteWorkflowPage;
